// src/taskpane/modules/view.ts
// [View Layer] 聚合 UI Utils, Logger
/* global document, HTMLElement, HTMLInputElement, HTMLButtonElement, HTMLTextAreaElement, window, localStorage, console */
import { DOM, ICONS,ModelProfile, I18N_DICT } from "./store";

// --- 1. Utils ---
export const Logger = {
    KEY: "bid_app_logs", MAX: 200,
    log(lvl: string, msg: string) {
        try {
            let logs = JSON.parse(localStorage.getItem(this.KEY) || "[]");
            logs.push(`[${new Date().toLocaleTimeString()}] [${lvl}] ${msg}`);
            if (logs.length > this.MAX) logs = logs.slice(-this.MAX);
            localStorage.setItem(this.KEY, JSON.stringify(logs));
            if(lvl==="ERROR") console.error(msg); else console.log(msg);
        } catch(e) {}
    },
    info(msg: string) { this.log("INFO", msg); },
    error(msg: string) { this.log("ERROR", msg); },
    export() { return (JSON.parse(localStorage.getItem(this.KEY) || "[]") as string[]).join("\n"); }
};

export function getEl<T extends HTMLElement>(id: string): T | null { return document.getElementById(id) as T | null; }
export function getSpacedKey(base: string, docId: string): string { return !docId ? base : `${docId}_${base}`; }

// --- 2. Base UI ---
export function setDisabled(elOrId: HTMLElement | string | null, disabled: boolean, opts?: { loadingText?: string }) {
    const el = typeof elOrId === 'string' ? getEl<HTMLElement>(elOrId) : elOrId;
    if (!el) return;
    
    if (disabled) {
        el.classList.add('force-disabled');
        // [关键修正] 只有当没有保存过 orig 时才保存，防止 "连接中..." 覆盖了 "测试并保存"
        if (!el.dataset._orig) el.dataset._orig = el.innerHTML;
        
        if (opts?.loadingText) el.innerHTML = opts.loadingText;
    } else {
        el.classList.remove('force-disabled');
        // 恢复原始文本
        if (el.dataset._orig) {
            el.innerHTML = el.dataset._orig;
            delete el.dataset._orig;
        }
    }
    
    if ('disabled' in el) (el as any).disabled = disabled;
    
    // Style adjustments
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.style.cursor = disabled ? 'wait' : '';
    else el.style.cursor = disabled ? 'not-allowed' : '';
    
    if (el instanceof HTMLButtonElement) el.style.opacity = disabled ? '0.5' : '';
}

export function updateFeedback(id: string, msg: string, isErr?: boolean) {
    const el = getEl(id); if (!el) return;
    const isSlim = id === DOM.STATUS_BAR;
    const color = isErr === true ? "#d13438" : (isErr === false ? "#107c10" : "#666");
    const icon = isErr === true ? ICONS.ERROR : (isErr === false ? ICONS.SUCCESS : "");
    
    if (id.includes("Feedback") || isSlim) {
        el.innerHTML = icon ? `<div style="display:flex;align-items:center;justify-content:flex-end;gap:4px;">${icon}<span>${msg}</span></div>` : msg;
        el.style.color = ""; el.className = isSlim ? "status-bar-slim" : "api-feedback";
        if (isErr !== undefined) el.classList.add(isErr ? "api-error" : "api-success");
    } else {
        el.innerText = msg; el.style.color = color;
    }
}

export function toggleLoading(loading: boolean) {
    document.querySelectorAll<HTMLButtonElement>(".async-btn").forEach(btn => setDisabled(btn, loading));
    const input = getEl<HTMLInputElement>(DOM.INSTRUCTION);
    if (input) { setDisabled(input, loading); input.style.backgroundColor = loading ? "#e6e6e6" : "#f3f2f1"; }
    // [修复] 补回 Dropdown 的交互控制
    const dropdown = getEl(DOM.DROPDOWN_TRIGGER);
    if (dropdown) { dropdown.style.pointerEvents = loading ? "none" : ""; dropdown.style.opacity = loading ? "0.5" : "1"; }
}

// --- 3. View Logic ---
export function setVisualMode(mode: "outline" | "content", lang: "en" | "zh") {
    const tab = getEl("tab-preview"); 
    const btn = getEl(DOM.BTN_INSERT); 
    const editor = getEl(DOM.FINAL_OUTPUT);
    const dict = I18N_DICT[lang];
    
    if (!tab) return;
    
    // 1. 基础样式清理逻辑（严格保留原功能）
    tab.classList.remove("mode-outline"); 
    editor?.classList.remove("mode-outline");
    if (btn) btn.classList.remove("mode-outline");

    if (mode === "outline") {
        // [i18n] 大纲模式文字
        tab.innerText = dict["tab-preview-outline"] || "Outline Preview"; 
        tab.classList.add("mode-outline"); 
        editor?.classList.add("mode-outline");
        if (btn) {
            // [i18n] 按钮文字由字典提供
            const btnText = dict["btn-insert-outline"] || "插入大纲";
            btn.innerHTML = `${ICONS.STRUCTURE}<span>${btnText}</span>`;
            btn.classList.add("mode-outline"); 
        }
    } else {
        // [i18n] 正文模式文字
        tab.innerText = dict["tab-preview"] || "正文预览"; 
        if (btn) {
            // [i18n] 按钮文字由字典提供
            const btnText = dict["btn-insert"] || "插入正文";
            btn.innerHTML = `${ICONS.INSERT}<span>${btnText}</span>`;
        }
    }
}

export function switchTab(target: "brain" | "preview", lang: "en" | "zh") {
    const isBrain = target === "brain";
    const dict = I18N_DICT[lang];
    if (!dict) return;
    
    // 1. DOM 状态切换
    getEl(DOM.TAB_BRAIN)?.classList.toggle("active", isBrain);
    getEl(DOM.TAB_PREVIEW)?.classList.toggle("active", !isBrain);
    getEl(DOM.BRAIN_OUTPUT)?.classList.toggle("hidden", !isBrain);
    getEl(DOM.FINAL_OUTPUT)?.classList.toggle("hidden", isBrain);
    
    // 2. [i18n] 状态栏反馈语切换
    const statusText = isBrain ? dict["status-thinking"] : dict["status-writing"];
    updateFeedback(DOM.STATUS_BAR, statusText);

    // 3. 滚动补偿逻辑（严格保留原功能）
    if (!isBrain) {
        setTimeout(() => getEl(DOM.FINAL_OUTPUT)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
}

export function updateBreadcrumb(html: string) {
    const el = getEl(DOM.BREADCRUMB);
    if (el) { el.innerHTML = html.replace(/<br\s*\/?>/gi, ICONS.CHEVRON); setTimeout(() => el.scrollLeft = el.scrollWidth, 50); }
    const icon = getEl("breadcrumb-icon"); if (icon && !icon.innerHTML) icon.innerHTML = ICONS.STRUCTURE;
}

/**
 * 初始化设置面板折叠行为
 */
export function initSettingsPanelBehavior(lang: "en" | "zh" = "zh") {
    // 获取当前页面上所有的旧 Header 节点
    const headers = document.querySelectorAll(".section-header");
    const dict = I18N_DICT[lang];

    headers.forEach((oldHeader) => {
        const el = oldHeader as HTMLElement;
        
        // 1. 【核心修复】克隆节点
        // 这会彻底剥离之前绑定的所有 click 事件，防止旧的闭包引用旧的 dict 变量
        const newHeader = el.cloneNode(true) as HTMLElement;
        
        // 用新的（干净的）节点替换旧节点
        el.parentNode?.replaceChild(newHeader, el);

        // 2. 立即更新静态文字状态
        // 这一步确保在用户点击之前，文字已经是正确的语言（展开/收起）
        const toggleText = newHeader.querySelector(".toggle-text");
        const targetId = newHeader.getAttribute("data-target");
        const content = getEl(targetId || "");
        
        if (content && toggleText) {
            // 根据当前 content 的实际显示状态（display），决定显示“展开”还是“收起”
            const isHidden = content.style.display === "none";
            toggleText.textContent = isHidden ? dict["toggle-expand"] : dict["toggle-collapse"];
        }

        // 3. 绑定新的点击事件
        // 这里的 dict 引用的是本次函数调用传入的最新语言包
        newHeader.addEventListener("click", () => {
            // 重新查询 DOM，因为其他 Header 也可能被 clone 替换了，需要获取最新的活体节点
            const currentHeaders = document.querySelectorAll(".section-header");
            
            currentHeaders.forEach((h) => {
                const hEl = h as HTMLElement;
                const tId = hEl.getAttribute("data-target");
                const c = getEl(tId || "");
                const tText = hEl.querySelector(".toggle-text");
                
                if (!c) return;

                if (hEl === newHeader) {
                    // === 当前点击的项：执行切换 ===
                    const isNowHidden = c.style.display === "none";
                    c.style.display = isNowHidden ? "block" : "none";
                    
                    // 更新文字为当前语言
                    if (tText) {
                        tText.textContent = isNowHidden ? dict["toggle-collapse"] : dict["toggle-expand"];
                    }
                } else {
                    // === 其他项：强制折叠 (手风琴效果) ===
                    c.style.display = "none";
                    if (tText) {
                        tText.textContent = dict["toggle-expand"];
                    }
                }
            });
        });
    });
}

// --- 5. Profile UI Logic (View Layer) ---

/**
 * 渲染模型列表 (支持双重状态：正在使用 vs 正在编辑)
 * @param profiles 数据源
 * @param activeId 正在使用的 ID (对应绿色/对勾)
 * @param editingId 正在编辑的 ID (对应蓝色高亮)
 * @param onEditCallback 点击回调
 */
export function renderProfileListUI(
    profiles: ModelProfile[], 
    activeId: string, 
    editingId: string, 
    onEditCallback: (p: ModelProfile) => void,
    lang: "en" | "zh" = "zh"
) {
    const container = getEl(DOM.LIST_Container); 
    if (!container) return;
    
    container.innerHTML = "";
    const dict = I18N_DICT[lang];

    profiles.forEach(p => {
        const item = document.createElement("div");
        const isActive = activeId === p.id;   // 正在使用 (Green)
        const isEditing = editingId === p.id; // 正在编辑 (Blue)
        let className = "profile-item";
        if (isActive) className += " current";
        if (isEditing) className += " active";
        
        item.className = className;
        let html = `<span>${p.name || (lang === "en" ? "Unnamed" : "未命名")}</span>`;
        if (isActive) {
            html += `<span style="float:right; font-weight:bold; font-size:12px;">${dict["status-using"]}</span>`;
        }
        item.innerHTML = html;
        
        item.onclick = (e) => {
            e.stopPropagation();
            onEditCallback(p);
        };
        
        container.appendChild(item);
    });

    if (profiles.length === 0) {
        container.innerHTML = `<div style="padding:10px;text-align:center;color:#999;">${lang === "en" ? "No Config" : "无配置"}</div>`;
    }
}

/**
 * 显示/填充表单
 */
export function openProfileFormUI(p: ModelProfile | null, lang: "en" | "zh" = "zh") {
    const isNew = !p;

    getEl(DOM.FORM_Container)!.style.display = "block";
    const btnAdd = getEl(DOM.BTN_ADD_PROFILE);
    if (btnAdd) btnAdd.style.display = "none";

    getEl<HTMLInputElement>(DOM.HIDDEN_ID)!.value = p ? p.id : "new_temp";
    getEl<HTMLInputElement>(DOM.INPUT_NAME)!.value = p ? p.name : "";
    getEl<HTMLInputElement>(DOM.INPUT_URL)!.value = p ? p.url : "";
    getEl<HTMLInputElement>(DOM.INPUT_MODEL)!.value = p ? p.model : "";
    getEl<HTMLInputElement>(DOM.INPUT_KEY)!.value = p ? p.apiKey : "";
    
    getEl(DOM.BTN_CANCEL_EDIT)!.style.display = "block";
    getEl("btn-del-profile")!.style.display = isNew ? "none" : "block";

    // [修复] 强制根据传入的 lang 重置测试按钮文字
    const btnTest = getEl(DOM.BTN_TEST);
    const dict = I18N_DICT[lang]; // 获取字典
    
    if (btnTest) {
        btnTest.classList.remove("btn-status-success");
        // 核心修正：无论之前 dataset 里存了什么，打开表单时都强制用当前语言刷新文字
        btnTest.innerHTML = `<span>${dict["btn-test-api"]}</span>`;
        
        // 如果有残留的缓存，同步更新缓存，防止 applyLanguageUI 覆盖回去
        if (btnTest.dataset._orig) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = btnTest.dataset._orig;
            const span = tempDiv.querySelector("span");
            if(span) span.innerText = dict["btn-test-api"];
            btnTest.dataset._orig = tempDiv.innerHTML;
        }
        
        setDisabled(btnTest, false); 
    }

    updateFeedback(DOM.API_FEEDBACK, "");
}

/**
 * 隐藏表单
 */
export function closeProfileFormUI() {
    getEl(DOM.FORM_Container)!.style.display = "none";
    
    const btnAdd = getEl(DOM.BTN_ADD_PROFILE);
    if (btnAdd) btnAdd.style.display = "flex";
}

/**
 * 获取表单当前的数据 (供 Controller 保存时读取)
 */
export function getProfileFormData(): ModelProfile {
    return {
        id: getEl<HTMLInputElement>(DOM.HIDDEN_ID)?.value || "",
        name: getEl<HTMLInputElement>(DOM.INPUT_NAME)?.value || "",
        url: getEl<HTMLInputElement>(DOM.INPUT_URL)?.value || "",
        model: getEl<HTMLInputElement>(DOM.INPUT_MODEL)?.value || "",
        apiKey: getEl<HTMLInputElement>(DOM.INPUT_KEY)?.value || ""
    };
}

// [新增] 专门用于绑定名称输入框的实时同步逻辑
export function bindProfileNameSync(onNameChange: (newName: string) => void) {
    const input = getEl<HTMLInputElement>(DOM.INPUT_NAME);
    if (input) {
        // 每次聚焦时先移除旧监听器 (防止多次绑定堆积)
        const newInput = input.cloneNode(true) as HTMLInputElement;
        input.parentNode?.replaceChild(newInput, input);
        
        newInput.addEventListener("input", () => {
            onNameChange(newInput.value);
        });
        
        // 恢复焦点，因为 replaceChild 会让焦点丢失
        newInput.focus(); 
    }
}

// [新增] 专门用于初始化输入框的事件处理 (View 层独有)有输入时禁用底部插入按钮
export function initInputHandlers(onSubmit: () => void, lang: "en" | "zh" = "zh") {
    const input = getEl<HTMLInputElement>(DOM.INSTRUCTION);
    const btnInsert = getEl<HTMLButtonElement>(DOM.BTN_INSERT);
    const btnSubmit = getEl<HTMLButtonElement>(DOM.BTN_SUBMIT_REVISE);

    if (!input) return;

    // 1. 快捷键绑定 (保持原逻辑)
    input.addEventListener("keydown", (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            if (btnSubmit && !btnSubmit.disabled) onSubmit();
        }
    });

    // 2. 防误触逻辑
    input.addEventListener("input", () => {
        const hasText = input.value.trim().length > 0;
        if (btnInsert) {
            if (hasText) {
                btnInsert.classList.add('force-disabled');
                btnInsert.style.opacity = "0.4";
                btnInsert.style.pointerEvents = "none";
            } else {
                btnInsert.classList.remove('force-disabled');
                btnInsert.style.opacity = "";
                btnInsert.style.pointerEvents = "";
            }
        }
    });
}

/**
 * 同步 UI 状态
 * @param state UI 状态对象
 * @param state.isRunning 是否正在运行
 * @param state.isHeading 是否在标题位置
 */
export function syncUI(state: { isRunning: boolean, isHeading: boolean }) {
    const btnSubmit = getEl<HTMLButtonElement>(DOM.BTN_SUBMIT_REVISE);
    
    // 第一优先级：任务运行中
    if (state.isRunning) {
        // 锁定全局交互
        toggleLoading(true);
        // 强制禁用所有业务按钮
        setDisabled(DOM.BTN_GENERATE, true);
        setDisabled(DOM.BTN_OUTLINE, true);
        setDisabled(DOM.BTN_POLISH, true);
        setDisabled(DOM.BTN_CHECK, true);
        // 切换至"停止"图标动画
        if (btnSubmit) {
            btnSubmit.classList.add('is-running');
        }
    } 
    // 第二优先级：任务未运行
    else {
        // 恢复全局交互
        toggleLoading(false);
        // 恢复"纸飞机"图标，停止动画
        if (btnSubmit) {
            btnSubmit.classList.remove('is-running');
        }
        
        // 上下文感应：根据标题状态切换按钮可用性
        if (state.isHeading) {
            // 在标题位置：启用Generate/Outline，禁用Polish/Check
            setDisabled(DOM.BTN_GENERATE, false);
            setDisabled(DOM.BTN_OUTLINE, false);
            setDisabled(DOM.BTN_POLISH, true);
            setDisabled(DOM.BTN_CHECK, true);
        } else {
            // 在正文位置：禁用Generate/Outline，启用Polish/Check
            setDisabled(DOM.BTN_GENERATE, true);
            setDisabled(DOM.BTN_OUTLINE, true);
            setDisabled(DOM.BTN_POLISH, false);
            setDisabled(DOM.BTN_CHECK, false);
        }
    }
}
